/**
 * AttachmentModal - Modal for adding attachments to nodes
 */

import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Image, Loader2, Trash2, Paperclip, FileText, Table } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';
import { isPDF } from '../../services/pdfService';
import { isExcel } from '../../services/excelService';

const AttachmentModal = () => {
  const {
    attachmentModalNodeId,
    closeAttachmentModal,
    addAttachment,
    deleteAttachment,
    nodes,
    isUploadingAttachment,
  } = useMapStore();

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Find the current node
  const currentNode = nodes.find(n => n.id === attachmentModalNodeId);
  const attachments = currentNode?.data?.attachments || [];

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Allow images, PDFs, and Excel files
    const isImage = file.type.startsWith('image/');
    const isPDFFile = isPDF(file);
    const isExcelFile = isExcel(file);

    if (!isImage && !isPDFFile && !isExcelFile) {
      alert('Sadece resim, PDF ve Excel dosyaları destekleniyor.');
      return;
    }

    setSelectedFile(file);

    // Create preview for images only
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else if (isPDFFile) {
      setPreview('pdf');
    } else if (isExcelFile) {
      setPreview('excel');
    }
  }, []);

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !attachmentModalNodeId) return;

    try {
      await addAttachment(attachmentModalNodeId, selectedFile);
      setSelectedFile(null);
      setPreview(null);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Dosya yüklenirken hata oluştu.');
    }
  };

  // Handle delete
  const handleDelete = async (attachmentId) => {
    if (!confirm('Bu eki silmek istediğinizden emin misiniz?')) return;

    try {
      await deleteAttachment(attachmentModalNodeId, attachmentId);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  if (!attachmentModalNodeId) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && closeAttachmentModal()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <Paperclip size={20} className="text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Dosya Ekleri</h2>
              <p className="text-xs text-gray-500">{currentNode?.data?.title || 'Node'}</p>
            </div>
          </div>
          <button
            onClick={closeAttachmentModal}
            className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Existing attachments */}
          {attachments.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-400">Mevcut Ekler</h3>
              <div className="grid grid-cols-2 gap-2">
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    className="relative group rounded-lg overflow-hidden bg-gray-800 border border-gray-700"
                  >
                    {att.file_type?.startsWith('image/') ? (
                      <img
                        src={att.file_url}
                        alt={att.file_name}
                        className="w-full h-24 object-cover"
                      />
                    ) : att.file_type === 'application/pdf' ? (
                      <div className="w-full h-24 flex flex-col items-center justify-center gap-1 bg-gray-800">
                        <FileText size={24} className="text-red-400" />
                        <span className="text-[10px] text-gray-500">PDF</span>
                      </div>
                    ) : att.file_type?.includes('spreadsheet') || att.file_type?.includes('excel') || att.file_name?.endsWith('.xlsx') || att.file_name?.endsWith('.xls') || att.file_name?.endsWith('.csv') ? (
                      <div className="w-full h-24 flex flex-col items-center justify-center gap-1 bg-gray-800">
                        <Table size={24} className="text-green-400" />
                        <span className="text-[10px] text-gray-500">Excel</span>
                      </div>
                    ) : (
                      <div className="w-full h-24 flex items-center justify-center">
                        <Paperclip size={24} className="text-gray-500" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => handleDelete(att.id)}
                        className="p-2 bg-red-500/80 rounded-full hover:bg-red-500 transition-colors"
                      >
                        <Trash2 size={14} className="text-white" />
                      </button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gray-900/80 text-xs text-gray-300 truncate">
                      {att.file_name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload new attachment */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-400">Yeni Ek Ekle</h3>

            {preview ? (
              <div className="relative rounded-lg overflow-hidden">
                {preview === 'pdf' ? (
                  // PDF Preview
                  <div className="w-full h-48 bg-gray-800 flex flex-col items-center justify-center gap-3 border border-gray-700">
                    <FileText size={48} className="text-red-400" />
                    <div className="text-center">
                      <p className="text-sm text-white font-medium truncate max-w-[250px] px-4">
                        {selectedFile?.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedFile && (selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                ) : preview === 'excel' ? (
                  // Excel Preview
                  <div className="w-full h-48 bg-gray-800 flex flex-col items-center justify-center gap-3 border border-gray-700">
                    <Table size={48} className="text-green-400" />
                    <div className="text-center">
                      <p className="text-sm text-white font-medium truncate max-w-[250px] px-4">
                        {selectedFile?.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedFile && (selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                ) : (
                  // Image Preview
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-48 object-cover"
                  />
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreview(null);
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-gray-900/80 rounded-full hover:bg-gray-900 transition-colors"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-32 flex flex-col items-center justify-center gap-2 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
              >
                <Upload size={24} className="text-gray-500" />
                <span className="text-sm text-gray-400">
                  Dosya yüklemek için tıklayın
                </span>
                <span className="text-xs text-gray-500">
                  PNG, JPG, PDF, Excel (max 10MB)
                </span>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.pdf,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              onChange={handleFileSelect}
              className="hidden"
            />

            {selectedFile && (
              <button
                onClick={handleUpload}
                disabled={isUploadingAttachment}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploadingAttachment ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Yükleniyor...</span>
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    <span>Yükle</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AttachmentModal;
